import React from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import { autobind } from 'core-decorators';
import moment from 'moment';
import BaseComponent from 'components/BaseComponent';
import styledComp from 'components/StyledComp';
import DataTables from 'components-desktop/DataTables';
import misc from 'misc/misc';
import helpers from 'misc/helpers';

const propTypes = {
  title: PropTypes.string,
  services: PropTypes.array,
};

const defaultProps = {
  title: '',
  services: [],
};

@autobind
class TableServices extends BaseComponent {
  constructor(props) {
    super(props);

    // Create group states to put them into this.state.
    const nextRateGroupState = helpers.createGroupState(
      props.services,
      'nextRate',
      s => s.getDouble('nextUnitRate'),
    );
    const nextRateTimeGroupState = helpers.createGroupState(
      props.services,
      'nextRateTime',
      s => s.getString('nextUnitRateTimeString') &&
        moment(s.getString('nextUnitRateTimeString'), 'YYYY-MM-DD HH:mm:ss'),
    );

    this.state = { ...nextRateGroupState, ...nextRateTimeGroupState };
  }

  onNextRateChange(newValue, serviceId) {
    this.setState({
      [`nextRate-${serviceId}`]: newValue,
    });
  }

  onNextRateTimeChange(newDateTime, serviceId) {
    this.setState({
      [`nextRateTime-${serviceId}`]: newDateTime,
    });
  }

  async onSaveServiceClick(record) {
    const params = {
      id: record.id,
      nextUnitRate: this.state[`nextRate-${record.id}`],
      nextUnitRateTime: misc.toDateTimeString(this.state[`nextRateTime-${record.id}`]),
    };
    await this.callService('/service/update_next_unit_rate', params);

    this.notifySuccess('Success', 'A new unit rate has been successfully saved.');
  }

  createColumns() {
    return [{
      title: 'Id',
      dataIndex: 'id',
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: 'descend',
    }, {
      title: 'Code',
      dataIndex: 'displayCode',
      sorter: (a, b) => a.displayCode?.localeCompare(b.displayCode),
    }, {
      title: 'Unit Type',
      dataIndex: 'unitType',
      sorter: (a, b) => a.unitType?.localeCompare(b.unitType),
    }, {
      title: 'Unit Multiplier',
      dataIndex: 'unitMultiplier',
      sorter: (a, b) => a.unitMultiplier - b.unitMultiplier,
    }, {
      title: 'Revenue Code',
      dataIndex: 'revenueCode',
      sorter: (a, b) => a.revenueCode?.localeCompare(b.revenueCode),
    }, {
      title: 'Description',
      dataIndex: 'description',
      sorter: (a, b) => a.description?.localeCompare(b.description),
    }, {
      title: 'Previous Rate',
      dataIndex: 'prevUnitRate',
      sorter: (a, b) => a.prevUnitRate - b.prevUnitRate,
    }, {
      title: 'Current Rate',
      dataIndex: 'unitRate',
      sorter: (a, b) => a.unitRate - b.unitRate,
    }, {
      title: 'New Rate',
      render: (text, record) => (
        <styledComp.AntdInput
          width="150px"
          height="wrap_content"
          color="textPrimaryDark"
          size="small"
          placeholder=""
          value={this.state[`nextRate-${record.id}`]}
          onChange={e => this.onNextRateChange(e.target.value, record.id)}
        />
      ),
    }, {
      title: 'New Rate Time',
      render: (text, record) => (
        <DatePicker
          style={{
            width: '100%',
          }}
          placeholder="MM/DD/YYYY hh:mm:ss"
          value={this.state[`nextRateTime-${record.id}`]}
          showTime={{
            format: 'HH:mm:ss',
            defaultValue: moment('00:00:00', 'HH:mm:ss'),
          }}
          format="MM/DD/YYYY HH:mm:ss"
          onChange={(date, dateString) => this.onNextRateTimeChange(date, record.id)}
        />
      ),
    }, {
      title: 'Actions',
      render: (text, record) => (
        <styledComp.A
          width="wrap_content"
          height="wrap_content"
          fontSize="12px"
          onClick={() => this.onSaveServiceClick(record)}
        >
          Save
        </styledComp.A>
      ),
    }];
  }

  createDataSource(services) {
    const dataSource = [];
    services.forEach((service, index) => {
      dataSource.push({
        key: service.getId(),
        id: service.getId(),
        displayCode: service.getString('displayCode'),
        unitType: this.e2t('serviceUnitType', service.getString('unitType')),
        unitMultiplier: service.getLong('unitMultiplier'),
        revenueCode: service.getString('revenueCode'),
        description: service.getString('description'),
        prevUnitRate: service.getDouble('prevUnitRate'),
        unitRate: service.getDouble('unitRate'),
      });
    });
    return dataSource;
  }

  renderContent() {
    const { services, title, ...layoutProps } = this.props;

    return (
      <DataTables
        {...layoutProps}
        ref={(ref) => { this.dataTables = ref; }}
        color="blue_hoki"
        fontSize="12px"
        size="small"
        title={title}
        icon="profile"
        withColumnFilter={false}
        columns={this.createColumns()}
        dataSource={this.createDataSource(services)}
      />
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
TableServices.propTypes = propTypes;
TableServices.defaultProps = defaultProps;

export default TableServices;
